import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import ScrollToTopButton from '../components/ScrollToTopButton';

// Global wrapper to prevent horizontal overflow
const GlobalWrapper = styled.div`
  overflow-x: hidden;
  width: 100%;
  position: relative;
`;

// Container component for consistent layout
const Container = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 15px;
  box-sizing: border-box;
`;

// Styled Components
const PageTitleSection = styled.section`
  padding: 50px 0 30px;
  background-color: white;
  text-align: center;
  width: 100%;
  
  h1 {
    color: var(--primary-color);
    font-size: 2.5rem;
    margin-bottom: 15px;
    word-wrap: break-word;
    
    @media (max-width: 768px) {
      font-size: 2rem;
    }
  }
  
  p {
    font-size: 1.2rem;
    color: var(--text-color);
    word-wrap: break-word;
    
    @media (max-width: 768px) {
      font-size: 1rem;
    }
  }
`;

const PageContentSection = styled.section`
  padding: 40px 0 60px;
  background-color: #f5f5f5;
  width: 100%;
  
  @media (max-width: 768px) {
    padding: 30px 0 40px;
  }
`;

const ContentContainer = styled.div`
  background-color: white;
  border-radius: 10px;
  box-shadow: var(--shadow);
  padding: 40px;
  max-width: 1000px;
  margin: 0 auto;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const ContentSection = styled.div`
  margin-bottom: 40px;
  width: 100%;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  h2 {
    color: var(--primary-color);
    font-size: 1.8rem;
    margin-bottom: 20px;
    padding-bottom: 10px;
    border-bottom: 2px solid var(--accent-color);
    word-wrap: break-word;
    
    @media (max-width: 768px) {
      font-size: 1.5rem;
    }
  }
  
  h3 {
    color: var(--primary-color);
    font-size: 1.4rem;
    margin: 25px 0 15px;
    word-wrap: break-word;
    
    @media (max-width: 768px) {
      font-size: 1.2rem;
    }
  }
  
  p {
    margin-bottom: 15px;
    line-height: 1.6;
    word-wrap: break-word;
  }
  
  ul {
    margin: 15px 0;
    padding-left: 20px;
    
    li {
      margin-bottom: 8px;
      word-wrap: break-word;
      
      @media (max-width: 768px) {
        padding-right: 5px;
      }
    }
  }
  
  strong {
    font-weight: 600;
  }
  
  a {
    color: var(--primary-color);
    text-decoration: none;
    word-wrap: break-word;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

function Datenschutz() {
  return (
    <GlobalWrapper>
      <PageTitleSection className="page-title">
        <Container>
          <h1>Datenschutzerklärung</h1>
          <p>Informationen zur Verarbeitung Ihrer personenbezogenen Daten</p>
        </Container>
      </PageTitleSection>

      <PageContentSection className="page-content">
        <Container>
          <ContentContainer className="content-container">
            <ContentSection className="content-section">
              <h2>1. Verantwortlicher und Überblick</h2>
              <p>Verantwortlich für die Datenverarbeitung auf dieser Website im Sinne der Datenschutz-Grundverordnung (DSGVO) ist:</p>
              <p>
                <strong>GuardiansMoves</strong><br />
                Vertreten durch: Galal Aldien Mghames<br />
                Müllerstr. 3<br />
                13353 Berlin<br /><br />
                Telefon: +49 155 6354 7378<br />
                E-Mail: info@guardiansmoves.de
              </p>
              <p>Diese Datenschutzerklärung informiert Sie über Art, Umfang und Zweck der Verarbeitung von personenbezogenen Daten auf unserer Webseite sowie über die Ihnen zustehenden Rechte.</p>
            </ContentSection>

            <ContentSection className="content-section">
              <h2>2. Datenerfassung auf unserer Website</h2>
              
              <h3>2.1 Informatorische Nutzung</h3>
              <p>Bei der rein informatorischen Nutzung unserer Website, also wenn Sie uns nicht anderweitig Informationen übermitteln, erheben wir nur solche Daten, die Ihr Browser an unseren Server übermittelt (sog. "Server-Logfiles"). Diese umfassen:</p>
              <ul>
                <li>Browsertyp und -version</li>
                <li>Verwendetes Betriebssystem</li>
                <li>Referrer URL (die zuvor besuchte Seite)</li>
                <li>Hostname des zugreifenden Rechners (IP-Adresse)</li>
                <li>Uhrzeit der Serveranfrage</li>
                <li>Übertragene Datenmenge</li>
              </ul>
              <p>Diese Daten sind technisch notwendig, um Ihnen unsere Website anzuzeigen und die Stabilität und Sicherheit zu gewährleisten. Die Rechtsgrundlage für die Datenverarbeitung ist Art. 6 Abs. 1 S. 1 lit. f DSGVO (berechtigtes Interesse). Die Logfiles werden nach spätestens 14 Tagen gelöscht.</p>

              <h3>2.2 Kontaktformular</h3>
              <p>Wenn Sie das Kontaktformular auf unserer Website nutzen, erheben wir die darin eingegebenen Daten (Name, E-Mail-Adresse, Telefon, Betreff und Ihre Nachricht). Wir speichern und verwenden diese Daten ausschließlich zur Bearbeitung Ihrer Anfrage. Die Rechtsgrundlage für die Verarbeitung ist Art. 6 Abs. 1 lit. b DSGVO (Vertragsanbahnung/Vertragserfüllung) oder Art. 6 Abs. 1 lit. a DSGVO (Einwilligung).</p>
              <p>Die von Ihnen im Kontaktformular eingegebenen Daten verbleiben bei uns, bis Sie uns zur Löschung auffordern, Ihre Einwilligung zur Speicherung widerrufen oder der Zweck für die Datenspeicherung entfällt (z.B. nach abgeschlossener Bearbeitung Ihrer Anfrage). Zwingende gesetzliche Bestimmungen -- insbesondere Aufbewahrungsfristen -- bleiben unberührt.</p>
            </ContentSection>

            <ContentSection className="content-section">
              <h2>3. Datenverarbeitung bei Bewerbungen</h2>
              <p>Wenn Sie sich über unsere Website oder per E-Mail bei uns bewerben, verarbeiten wir die von Ihnen übermittelten personenbezogenen Daten (wie Name, Kontaktdaten, Bewerbungsunterlagen, Qualifikationen, etc.) zum Zwecke der Durchführung des Bewerbungsverfahrens.</p>
              <p>Die Rechtsgrundlage für diese Verarbeitung ist § 26 BDSG (neu) und Art. 6 Abs. 1 lit. b DSGVO (vorvertragliche Maßnahme).</p>
              <p>Im Falle einer Ablehnung speichern wir Ihre Bewerbungsdaten für bis zu sechs Monate nach Bekanntgabe der Absage, um etwaige Ansprüche aus dem Allgemeinen Gleichbehandlungsgesetz (AGG) erfüllen zu können. Danach werden die Daten gelöscht, es sei denn, Sie haben einer längeren Speicherung für zukünftige Stellenausschreibungen zugestimmt (Talent Pool).</p>
            </ContentSection>

            <ContentSection className="content-section">
              <h2>4. Cookies und Analyse-Tools</h2>
              <p>Unsere Website verwendet Cookies. Dabei handelt es sich um kleine Textdateien, die auf Ihrem Endgerät gespeichert werden. Manche Cookies sind notwendig, um die Funktionalität unserer Website zu gewährleisten (technisch notwendige Cookies).</p>
              <p>Wir nutzen folgende Arten von Cookies:</p>
              <ul>
                <li><strong>Technisch notwendige Cookies:</strong> Diese sind für den Betrieb der Website erforderlich und können in Ihren Systemeinstellungen nicht deaktiviert werden. Die Rechtsgrundlage für diese Cookies ist Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse).</li>
              </ul>
              <p>Sie können Ihren Browser so einstellen, dass er Sie über das Setzen von Cookies informiert oder Cookies generell ablehnt. Ohne technisch notwendige Cookies kann die Website jedoch möglicherweise nicht richtig funktionieren.</p>
            </ContentSection>

            <ContentSection className="content-section">
              <h2>5. Datenweitergabe</h2>
              <p>Eine Übermittlung Ihrer persönlichen Daten an Dritte zu anderen als den im Folgenden aufgeführten Zwecken findet nicht statt.</p>
              <p>Wir geben Ihre persönlichen Daten nur an Dritte weiter, wenn:</p>
              <ul>
                <li>Sie Ihre ausdrückliche Einwilligung dazu erteilt haben (Art. 6 Abs. 1 lit. a DSGVO),</li>
                <li>die Weitergabe zur Geltendmachung, Ausübung oder Verteidigung von Rechtsansprüchen erforderlich ist und kein Grund zur Annahme besteht, dass Sie ein überwiegendes schutzwürdiges Interesse an der Nichtweitergabe Ihrer Daten haben (Art. 6 Abs. 1 lit. f DSGVO),</li>
                <li>für die Weitergabe eine gesetzliche Verpflichtung besteht (Art. 6 Abs. 1 lit. c DSGVO), oder</li>
                <li>dies gesetzlich zulässig und für die Abwicklung von Vertragsverhältnissen mit Ihnen erforderlich ist (Art. 6 Abs. 1 lit. b DSGVO).</li>
              </ul>
            </ContentSection>

            <ContentSection className="content-section">
              <h2>6. Ihre Rechte</h2>
              <p>Sie haben gegenüber uns folgende Rechte hinsichtlich der Sie betreffenden personenbezogenen Daten:</p>
              <ul>
                <li><strong>Recht auf Auskunft</strong> (Art. 15 DSGVO): Sie können jederzeit Auskunft darüber verlangen, ob und welche Daten wir von Ihnen verarbeiten.</li>
                <li><strong>Recht auf Berichtigung</strong> (Art. 16 DSGVO): Sie können die Berichtigung unrichtiger oder die Vervollständigung Ihrer bei uns gespeicherten personenbezogenen Daten verlangen.</li>
                <li><strong>Recht auf Löschung</strong> (Art. 17 DSGVO): Sie können die Löschung Ihrer bei uns gespeicherten personenbezogenen Daten verlangen, soweit nicht die Verarbeitung zur Ausübung des Rechts auf freie Meinungsäußerung und Information, zur Erfüllung einer rechtlichen Verpflichtung, aus Gründen des öffentlichen Interesses oder zur Geltendmachung, Ausübung oder Verteidigung von Rechtsansprüchen erforderlich ist.</li>
                <li><strong>Recht auf Einschränkung</strong> (Art. 18 DSGVO): Sie können die Einschränkung der Verarbeitung Ihrer personenbezogenen Daten verlangen.</li>
                <li><strong>Recht auf Datenübertragbarkeit</strong> (Art. 20 DSGVO): Sie können verlangen, dass wir Ihnen Ihre personenbezogenen Daten, die Sie uns bereitgestellt haben, in einem strukturierten, gängigen und maschinenlesbaren Format übermitteln.</li>
                <li><strong>Recht auf Widerruf von Einwilligungen</strong> (Art. 7 Abs. 3 DSGVO): Sie können eine erteilte Einwilligung jederzeit widerrufen.</li>
                <li><strong>Recht auf Beschwerde</strong>: Sie haben das Recht, sich bei einer Aufsichtsbehörde zu beschweren. In der Regel können Sie sich hierfür an die Aufsichtsbehörde Ihres üblichen Aufenthaltsortes oder Arbeitsplatzes wenden.</li>
              </ul>
              <p>Möchten Sie von Ihrem Widerrufs- oder Widerspruchsrecht Gebrauch machen, genügt eine E-Mail an: info@guardiansmoves.de</p>
            </ContentSection>

            <ContentSection className="content-section">
              <h2>7. Datensicherheit</h2>
              <p>Wir treffen nach Maßgabe des Art. 32 DSGVO unter Berücksichtigung des Stands der Technik, der Implementierungskosten und der Art, des Umfangs, der Umstände und der Zwecke der Verarbeitung sowie der unterschiedlichen Eintrittswahrscheinlichkeit und Schwere des Risikos für die Rechte und Freiheiten natürlicher Personen, geeignete technische und organisatorische Maßnahmen, um ein dem Risiko angemessenes Schutzniveau zu gewährleisten.</p>
              <p>Zu den Maßnahmen gehören insbesondere die Sicherung der Vertraulichkeit, Integrität und Verfügbarkeit von Daten durch Kontrolle des physischen Zugangs zu den Daten, sowie des sie betreffenden Zugriffs, der Eingabe, Weitergabe, der Sicherung der Verfügbarkeit und ihrer Trennung. Wir haben Verfahren eingerichtet, die eine Wahrnehmung von Betroffenenrechten, Löschung von Daten und Reaktion auf Gefährdung der Daten gewährleisten.</p>
            </ContentSection>

            <ContentSection className="content-section">
              <h2>8. Aktualität und Änderung dieser Datenschutzerklärung</h2>
              <p>Diese Datenschutzerklärung ist aktuell gültig und hat den Stand: April 2025.</p>
              <p>Durch die Weiterentwicklung unserer Website und Angebote oder aufgrund geänderter gesetzlicher bzw. behördlicher Vorgaben kann es notwendig werden, diese Datenschutzerklärung zu ändern. Die jeweils aktuelle Datenschutzerklärung kann jederzeit auf dieser Website eingesehen werden.</p>
            </ContentSection>
          </ContentContainer>
        </Container>
      </PageContentSection>
      
      <ScrollToTopButton />
    </GlobalWrapper>
  );
}

export default Datenschutz;